import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, FileText, Shield, AlertTriangle, ClipboardList,
  TrendingUp, LogOut, Eye, Calendar, MapPin, Activity, Cloud, X, Download
} from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import ViewAuditDialog from '@/components/ViewAuditDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const SuperintendentDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  const [jsas, setJsas] = useState([]);
  const [audits, setAudits] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [fieldLogs, setFieldLogs] = useState([]);
  const [workforce, setWorkforce] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Detail view state
  const [viewingJsa, setViewingJsa] = useState(null);
  const [viewingAudit, setViewingAudit] = useState(null);
  const [viewingIncident, setViewingIncident] = useState(null);
  const [viewingFieldLog, setViewingFieldLog] = useState(null);

  // Report filters
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/superintendent/dashboard');
      setDashboard(data);
    } catch (err) {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadTab = async (tab) => {
    setActiveTab(tab);
    try {
      if (tab === 'supervisors' && supervisors.length === 0) {
        const { data } = await axiosInstance.get('/superintendent/supervisors');
        setSupervisors(data);
      } else if (tab === 'jsas' && jsas.length === 0) {
        const { data } = await axiosInstance.get('/superintendent/jsas');
        setJsas(data);
      } else if (tab === 'audits' && audits.length === 0) {
        const { data } = await axiosInstance.get('/superintendent/audits');
        setAudits(data);
      } else if (tab === 'incidents' && incidents.length === 0) {
        const { data } = await axiosInstance.get('/superintendent/incidents');
        setIncidents(data);
      } else if (tab === 'field-logs' && fieldLogs.length === 0) {
        const { data } = await axiosInstance.get('/superintendent/field-logs');
        setFieldLogs(data);
      } else if (tab === 'workforce' && !workforce) {
        const { data } = await axiosInstance.get('/superintendent/workforce');
        setWorkforce(data);
      }
    } catch {
      toast.error(t('common.loadFailed'));
    }
  };

  const handleExportPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (reportDateFrom) params.append('date_from', reportDateFrom);
      if (reportDateTo) params.append('date_to', reportDateTo);
      const response = await axiosInstance.get(`/superintendent/export/division-report-pdf?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `division_report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('superintendent.reportDownloaded'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleExportCsv = async (type = 'all') => {
    try {
      const params = new URLSearchParams({ report_type: type });
      if (reportDateFrom) params.append('date_from', reportDateFrom);
      if (reportDateTo) params.append('date_to', reportDateTo);
      const response = await axiosInstance.get(`/superintendent/export/division-report-csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `division_report_${type}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('superintendent.reportDownloaded'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const recent = dashboard?.recentActivity || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-xl">SM</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-600">SafetyMind AI</h1>
                <p className="text-xs text-slate-400">Safety That Thinks</p>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="superintendent-title">
              {t('superintendent.welcome')}, {user?.name}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('superintendent.role')} — {dashboard?.divisionName || t('superintendent.allDivisions')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button variant="outline" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="stat-supervisors">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalSupervisors || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.supervisors')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-workers">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg"><Users className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalWorkers || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.workers')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-jsas">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg"><FileText className="w-5 h-5 text-orange-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalJsas || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.jsas')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-audits">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><Shield className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalAudits || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.audits')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalIncidents || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.incidents')}</p>
                  {stats.openIncidents > 0 && (
                    <Badge className="bg-red-100 text-red-700 mt-1">{stats.openIncidents} {t('superintendent.open')}</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg"><ClipboardList className="w-5 h-5 text-teal-600" /></div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalFieldLogs || 0}</p>
                  <p className="text-xs text-gray-500">{t('superintendent.fieldLogs')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg"><Activity className="w-5 h-5 text-blue-700" /></div>
                <div>
                  <p className="text-sm font-semibold text-blue-900">{t('superintendent.last7Days')}</p>
                  <div className="flex gap-3 mt-1 text-xs text-blue-700">
                    <span>{recent.jsas || 0} JSAs</span>
                    <span>{recent.audits || 0} {t('superintendent.audits')}</span>
                    <span>{recent.incidents || 0} {t('superintendent.incidents')}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Export Reports Section */}
        <Card className="mb-8 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="w-4 h-4" />
              {t('superintendent.exportReports')}
            </CardTitle>
            <CardDescription>{t('superintendent.exportReportsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs">{t('fieldLogs.dateFrom')}</Label>
                <Input type="date" value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} className="w-40" data-testid="report-date-from" />
              </div>
              <div>
                <Label className="text-xs">{t('fieldLogs.dateTo')}</Label>
                <Input type="date" value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} className="w-40" data-testid="report-date-to" />
              </div>
              <Button onClick={handleExportPdf} className="bg-red-600 hover:bg-red-700" data-testid="export-pdf-btn">
                <Download className="w-4 h-4 mr-2" />
                {t('superintendent.fullReportPdf')}
              </Button>
              <Button variant="outline" onClick={() => handleExportCsv('all')} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                {t('superintendent.fullReportCsv')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExportCsv('jsas')}>JSAs CSV</Button>
              <Button variant="outline" size="sm" onClick={() => handleExportCsv('audits')}>{t('superintendent.audits')} CSV</Button>
              <Button variant="outline" size="sm" onClick={() => handleExportCsv('incidents')}>{t('superintendent.incidents')} CSV</Button>
              <Button variant="outline" size="sm" onClick={() => handleExportCsv('field-logs')}>{t('superintendent.fieldLogs')} CSV</Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={loadTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview">{t('superintendent.overview')}</TabsTrigger>
            <TabsTrigger value="supervisors">{t('superintendent.supervisors')}</TabsTrigger>
            <TabsTrigger value="jsas">JSAs</TabsTrigger>
            <TabsTrigger value="audits">{t('superintendent.audits')}</TabsTrigger>
            <TabsTrigger value="incidents">{t('superintendent.incidents')}</TabsTrigger>
            <TabsTrigger value="field-logs">{t('superintendent.fieldLogs')}</TabsTrigger>
            <TabsTrigger value="workforce">{t('superintendent.workforce')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>{t('superintendent.divisionSummary')}</CardTitle>
                <CardDescription>{dashboard?.divisionName}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(dashboard?.supervisors || []).map(sup => (
                    <div key={sup.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{sup.name || sup.email}</p>
                        <p className="text-xs text-gray-500">{sup.email}</p>
                      </div>
                      <Badge variant="outline">{t('superintendent.supervisor')}</Badge>
                    </div>
                  ))}
                  {(!dashboard?.supervisors || dashboard.supervisors.length === 0) && (
                    <p className="text-gray-500 text-center py-4">{t('superintendent.noSupervisors')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Supervisors Tab */}
          <TabsContent value="supervisors">
            <div className="space-y-3">
              {supervisors.map(sup => (
                <Card key={sup.id} data-testid={`supervisor-${sup.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{sup.name || sup.email}</h3>
                        <p className="text-sm text-gray-500">{sup.email} {sup.phone && `• ${sup.phone}`}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge className="bg-orange-100 text-orange-700">{sup.jsaCount} JSAs</Badge>
                        <Badge className="bg-green-100 text-green-700">{sup.auditCount} {t('superintendent.audits')}</Badge>
                        <Badge className="bg-red-100 text-red-700">{sup.incidentCount} {t('superintendent.incidents')}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {supervisors.length === 0 && (
                <p className="text-gray-500 text-center py-8">{t('superintendent.noSupervisors')}</p>
              )}
            </div>
          </TabsContent>

          {/* JSAs Tab */}
          <TabsContent value="jsas">
            <div className="space-y-3">
              {jsas.slice(0, 50).map(jsa => (
                <Card key={jsa.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setViewingJsa(jsa)} data-testid={`jsa-${jsa.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate">{jsa.taskDescription || jsa.task}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" /><span>{jsa.location}</span>
                          <Calendar className="w-3 h-3 ml-2" /><span>{jsa.date || jsa.createdAt?.split('T')[0]}</span>
                          <span className="ml-2">{t('common.by')} {jsa.supervisorName}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={jsa.status === 'active' ? 'default' : 'secondary'}>{jsa.status}</Badge>
                        <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {jsas.length === 0 && <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>}
            </div>
          </TabsContent>

          {/* Audits Tab */}
          <TabsContent value="audits">
            <div className="space-y-3">
              {audits.slice(0, 50).map(audit => (
                <Card key={audit.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setViewingAudit(audit)} data-testid={`audit-${audit.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium">{audit.location || t('common.unknown')}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" /><span>{audit.date || audit.createdAt?.split('T')[0]}</span>
                          <span className="ml-2">{t('common.by')} {audit.supervisorName}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {audit.aiAnalysis?.overallRiskLevel && (
                          <Badge className={
                            audit.aiAnalysis.overallRiskLevel.includes('High') ? 'bg-red-600' :
                            audit.aiAnalysis.overallRiskLevel.includes('Medium') ? 'bg-orange-500' : 'bg-green-600'
                          }>{audit.aiAnalysis.overallRiskLevel}</Badge>
                        )}
                        {audit.compliancePercentage != null && (
                          <Badge variant="outline">{Math.round(audit.compliancePercentage)}%</Badge>
                        )}
                        <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {audits.length === 0 && <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>}
            </div>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents">
            <div className="space-y-3">
              {incidents.slice(0, 50).map(inc => (
                <Card key={inc.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setViewingIncident(inc)} data-testid={`incident-${inc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{inc.incidentType}</h3>
                          <Badge className={
                            inc.severity === 'Critical' ? 'bg-red-600' :
                            inc.severity === 'High' ? 'bg-orange-600' :
                            inc.severity === 'Medium' ? 'bg-yellow-600' : 'bg-blue-600'
                          }>{inc.severity}</Badge>
                          <Badge variant={inc.status === 'submitted' ? 'default' : 'secondary'}>{inc.status}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <MapPin className="w-3 h-3" /><span>{inc.location}</span>
                          <Calendar className="w-3 h-3 ml-2" /><span>{inc.incidentDate}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-1">{inc.description}</p>
                      </div>
                      <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {incidents.length === 0 && <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>}
            </div>
          </TabsContent>

          {/* Field Logs Tab */}
          <TabsContent value="field-logs">
            <div className="space-y-3">
              {fieldLogs.slice(0, 50).map(log => (
                <Card key={log.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setViewingFieldLog(log)} data-testid={`fieldlog-${log.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline"><Calendar className="w-3 h-3 mr-1" />{log.date}</Badge>
                          <Badge className="bg-blue-100 text-blue-800"><MapPin className="w-3 h-3 mr-1" />{log.location}</Badge>
                          {log.crewSize && <Badge variant="outline"><Users className="w-3 h-3 mr-1" />{log.crewSize}</Badge>}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{log.workPerformed}</p>
                        {log.createdByName && (
                          <p className="text-xs text-gray-400 mt-1">{t('common.by')} {log.createdByName}</p>
                        )}
                      </div>
                      <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {fieldLogs.length === 0 && <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>}
            </div>
          </TabsContent>
          {/* Workforce Tab */}
          <TabsContent value="workforce">
            {workforce ? (
              <div className="space-y-6">
                {/* Workforce Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold">{workforce.summary.totalWorkers}</p>
                      <p className="text-xs text-gray-500">{t('superintendent.totalWorkers')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{workforce.summary.fullyCompliant}</p>
                      <p className="text-xs text-gray-500">{t('superintendent.fullyCompliant')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-orange-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">{workforce.summary.nonCompliant}</p>
                      <p className="text-xs text-gray-500">{t('superintendent.nonCompliant')}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{workforce.summary.expiredCerts}</p>
                      <p className="text-xs text-gray-500">{t('superintendent.expiredCerts')}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Expiring Certificates Alert */}
                {workforce.expiringCerts.length > 0 && (
                  <Card className="border-red-300 bg-red-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-red-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {t('superintendent.certAlerts')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {workforce.expiringCerts.slice(0, 15).map((cert, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div>
                              <span className="font-medium text-sm">{cert.workerName}</span>
                              <span className="text-gray-500 text-xs ml-2">— {cert.trainingName}</span>
                            </div>
                            <Badge className={cert.expired ? 'bg-red-600' : cert.daysUntil <= 30 ? 'bg-orange-500' : 'bg-yellow-500'}>
                              {cert.expired ? t('superintendent.expired') : `${cert.daysUntil} ${t('superintendent.daysLeft')}`}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Worker Compliance Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('superintendent.trainingCompliance')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {workforce.workers.map(w => (
                        <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`worker-${w.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{w.name || w.email}</p>
                            <p className="text-xs text-gray-500">{w.role} {w.craft && `• ${w.craft}`}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm font-medium">{w.completedCount}/{w.totalRequired}</p>
                              <p className="text-xs text-gray-500">{t('superintendent.coursesComplete')}</p>
                            </div>
                            <div className="w-20">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${w.compliancePercent === 100 ? 'bg-green-500' : w.compliancePercent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                  style={{ width: `${w.compliancePercent}%` }}
                                />
                              </div>
                              <p className="text-xs text-center mt-1">{w.compliancePercent}%</p>
                            </div>
                            {w.expiringCerts.length > 0 && (
                              <Badge className="bg-red-100 text-red-700">{w.expiringCerts.length} {t('superintendent.expiring')}</Badge>
                            )}
                            {w.drugScreenStatus && (
                              <Badge className={w.drugScreenStatus.expired ? 'bg-red-600' : w.drugScreenStatus.daysUntil <= 30 ? 'bg-orange-500' : 'bg-green-100 text-green-700'}>
                                {w.drugScreenStatus.expired ? t('superintendent.drugExpired') : `Drug: ${w.drugScreenStatus.daysUntil}d`}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {workforce.workers.length === 0 && (
                        <p className="text-gray-500 text-center py-4">{t('common.noData')}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">{t('common.loading')}</p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* JSA Detail Dialog */}
      <Dialog open={!!viewingJsa} onOpenChange={() => setViewingJsa(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingJsa && (
            <>
              <DialogHeader>
                <DialogTitle>{t('jsa.viewJsaTitle')}</DialogTitle>
                <DialogDescription>{viewingJsa.location} — {viewingJsa.date || viewingJsa.createdAt?.split('T')[0]}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('jsa.taskWorkerDoing')}</h4>
                  <p className="text-gray-900">{viewingJsa.taskDescription || viewingJsa.task}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">{t('common.location')}:</span> <span className="font-medium">{viewingJsa.location}</span></div>
                  <div><span className="text-gray-500">{t('common.date')}:</span> <span className="font-medium">{viewingJsa.date}</span></div>
                  <div><span className="text-gray-500">{t('incidents.supervisor')}:</span> <span className="font-medium">{viewingJsa.supervisorName}</span></div>
                  <div><span className="text-gray-500">{t('common.status')}:</span> <Badge variant={viewingJsa.status === 'active' ? 'default' : 'secondary'}>{viewingJsa.status}</Badge></div>
                </div>
                {viewingJsa.toolsAndChemicals && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('jsa.toolsChemicals')}</h4>
                    <p className="text-sm">{viewingJsa.toolsAndChemicals}</p>
                  </div>
                )}
                {viewingJsa.hazards?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-2">{t('incidents.hazardsAndControls')}</h4>
                    <div className="space-y-2">
                      {viewingJsa.hazards.map((h, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg border-l-4 border-orange-400">
                          <p className="font-medium text-sm text-red-800">{h.hazard || h.description}</p>
                          <p className="text-sm text-green-800 mt-1">{h.mitigation || h.control}</p>
                          {h.ppe && <p className="text-xs text-blue-700 mt-1">PPE: {h.ppe}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {viewingJsa.assignedWorkers?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('jsa.assignWorkersOptional')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {viewingJsa.assignedWorkers.map((w, i) => (
                        <Badge key={i} variant="outline">{w.name || w}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {viewingJsa.notes && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('jsa.additionalNotes')}</h4>
                    <p className="text-sm">{viewingJsa.notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingJsa(null)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Detail - reuse existing ViewAuditDialog */}
      {viewingAudit && (
        <ViewAuditDialog
          audit={viewingAudit}
          open={!!viewingAudit}
          onClose={() => setViewingAudit(null)}
          user={user}
        />
      )}

      {/* Incident Detail Dialog */}
      <Dialog open={!!viewingIncident} onOpenChange={() => setViewingIncident(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingIncident && (
            <>
              <DialogHeader>
                <DialogTitle>{t('incidents.viewIncident')}</DialogTitle>
                <DialogDescription>{viewingIncident.location} — {viewingIncident.incidentDate}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className={
                    viewingIncident.severity === 'Critical' ? 'bg-red-600' :
                    viewingIncident.severity === 'High' ? 'bg-orange-600' :
                    viewingIncident.severity === 'Medium' ? 'bg-yellow-600' : 'bg-blue-600'
                  }>{viewingIncident.severity}</Badge>
                  <Badge variant="outline">{viewingIncident.incidentType}</Badge>
                  <Badge variant={viewingIncident.status === 'submitted' ? 'default' : 'secondary'}>{viewingIncident.status}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">{t('common.location')}:</span> <span className="font-medium">{viewingIncident.location}</span></div>
                  <div><span className="text-gray-500">{t('common.date')}:</span> <span className="font-medium">{viewingIncident.incidentDate} {viewingIncident.incidentTime}</span></div>
                  {viewingIncident.supervisorName && <div><span className="text-gray-500">{t('incidents.supervisor')}:</span> <span className="font-medium">{viewingIncident.supervisorName}</span></div>}
                  {viewingIncident.reportedBy && <div><span className="text-gray-500">{t('incidents.reported')}:</span> <span className="font-medium">{viewingIncident.reportedBy}</span></div>}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('common.description')}</h4>
                  <p className="text-sm bg-gray-50 p-3 rounded">{viewingIncident.description}</p>
                </div>
                {viewingIncident.correctiveActions && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('incidents.correctiveActions')}</h4>
                    <p className="text-sm bg-green-50 p-3 rounded">{viewingIncident.correctiveActions}</p>
                  </div>
                )}
                {viewingIncident.preventiveMeasures && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('incidents.preventiveMeasures')}</h4>
                    <p className="text-sm bg-blue-50 p-3 rounded">{viewingIncident.preventiveMeasures}</p>
                  </div>
                )}
                {viewingIncident.personsInvolved?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('incidents.personsInvolved')}</h4>
                    <div className="space-y-1">
                      {viewingIncident.personsInvolved.map((p, i) => (
                        <div key={i} className="text-sm p-2 bg-gray-50 rounded">
                          <span className="font-medium">{p.name}</span> — {p.role} {p.details && `• ${p.details}`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {viewingIncident.witnessStatements?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('incidents.witnessStatements')}</h4>
                    <div className="space-y-2">
                      {viewingIncident.witnessStatements.map((w, i) => (
                        <div key={i} className="text-sm p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                          <p className="font-medium">{w.name}</p>
                          <p className="mt-1">{w.statement}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {viewingIncident.aiAnalysis && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-500 mb-1">{t('incidents.aiAnalysisResults')}</h4>
                    {viewingIncident.aiAnalysis.rootCauses?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-600 mb-1">{t('incidents.rootCauses')}</p>
                        {viewingIncident.aiAnalysis.rootCauses.map((rc, i) => (
                          <p key={i} className="text-sm text-gray-700">• {rc.category}: {rc.description}</p>
                        ))}
                      </div>
                    )}
                    {viewingIncident.aiAnalysis.lessonsLearned?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">{t('incidents.lessonsLearned')}</p>
                        {viewingIncident.aiAnalysis.lessonsLearned.map((ll, i) => (
                          <p key={i} className="text-sm text-gray-700">• [{ll.category}] {ll.lesson}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingIncident(null)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Field Log Detail Dialog */}
      <Dialog open={!!viewingFieldLog} onOpenChange={() => setViewingFieldLog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingFieldLog && (
            <>
              <DialogHeader>
                <DialogTitle>{t('fieldLogs.viewEntry')}</DialogTitle>
                <DialogDescription>{viewingFieldLog.date} — {viewingFieldLog.location}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{t('common.date')}:</span> {viewingFieldLog.date}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{t('common.location')}:</span> {viewingFieldLog.location}
                  </div>
                  {viewingFieldLog.weather && (
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{t('fieldLogs.weather')}:</span> {viewingFieldLog.weather}
                    </div>
                  )}
                  {viewingFieldLog.crewSize && (
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{t('fieldLogs.crewSize')}:</span> {viewingFieldLog.crewSize}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.workPerformed')}</h4>
                  <p className="text-sm bg-gray-50 p-3 rounded">{viewingFieldLog.workPerformed}</p>
                </div>
                {viewingFieldLog.delays && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.delays')}</h4>
                    <p className="text-sm bg-orange-50 p-3 rounded text-orange-800">{viewingFieldLog.delays}</p>
                  </div>
                )}
                {viewingFieldLog.visitors && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.visitors')}</h4>
                    <p className="text-sm bg-blue-50 p-3 rounded">{viewingFieldLog.visitors}</p>
                  </div>
                )}
                {viewingFieldLog.safetyObservations && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.safetyObservations')}</h4>
                    <p className="text-sm bg-green-50 p-3 rounded text-green-800">{viewingFieldLog.safetyObservations}</p>
                  </div>
                )}
                {viewingFieldLog.notes && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">{t('fieldLogs.generalNotes')}</h4>
                    <p className="text-sm bg-gray-50 p-3 rounded">{viewingFieldLog.notes}</p>
                  </div>
                )}
                {viewingFieldLog.createdByName && (
                  <p className="text-xs text-gray-400">{t('common.by')} {viewingFieldLog.createdByName} — {new Date(viewingFieldLog.createdAt).toLocaleString()}</p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingFieldLog(null)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperintendentDashboard;
